import React, { useEffect, useState } from "react";
import { Box, Divider, Grid, CircularProgress } from "@mui/material";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzSelectListPopupInput from "src/components/core-components/mui/inputs/ByzSelectListPopupInput";
import PatientDatas from "src/modules/medcard/patient-datas";
import { useAppSelector } from "src/app/hooks";
import { mcGeneralDiseaseService, McGeneralDiseaseItem } from "src/api/medcards/mcGeneralDiseaseService";
import ByzMuiSnackBar from "src/components/core-components/mui/ByzMuiSnackBar";
import { useTranslate } from "src/locales";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { parseISO } from "date-fns";

interface HabitsModalProps {
  open: boolean;
  onClose: () => void;
  patientId?: string | number;
  editData?: McGeneralDiseaseItem | null;
}

const HabitsModal: React.FC<HabitsModalProps> = ({
  open,
  onClose,
  patientId,
  editData,
}) => {
  const { t } = useTranslate();
  const { user } = useAppSelector((state) => (state as any).auth);
  const selectedOfficeId = useAppSelector((state) => (state as any).auth.selectedOfficeId);
  const selectedOffice = user?.UserAssPriviliges?.find((office: any) => office.ID === selectedOfficeId);
  const offid = Number(selectedOffice?.OFF_ID || user?.UserAssPriviliges?.[0]?.OFF_ID || 2);

  const [operationDate, setOperationDate] = useState<Date | null>(new Date());
  const [operationUser, setOperationUser] = useState<string>("");
  const [habit, setHabit] = useState<string>("");
  const [habitName, setHabitName] = useState<string>("");
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [type, setType] = useState<string>("");
  const [quitDate, setQuitDate] = useState<Date | null>(new Date());
  const [amountDaily, setAmountDaily] = useState<string>("");
  const [durationYear, setDurationYear] = useState<string>("");
  const [note, setNote] = useState<string>("");
  const [habitId, setHabitId] = useState<number | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState<"success" | "error">("success");
  const [loading, setLoading] = useState(false);

  // Kullanıcı adını otomatik olarak login durumundan al
  useEffect(() => {
    if (user?.username) {
      setOperationUser(user.username);
    }
  }, [user]);

  // Edit data varsa form'u doldur
  useEffect(() => {
    if (open && editData) {
      setHabitId(editData.pid || null);
      setHabitName(editData.name || "");
      setSelectedItem({
        id: editData.pid,
        name: editData.name,
        displayField: editData.name,
      });
      setType(editData.typeofmaterial || "");
      setAmountDaily(editData.dailyuseamount?.toString() || "");
      setDurationYear(editData.usagetime?.toString() || "");
      setNote(editData.note || editData.privatenotes || "");
      if (editData.cessationdate) {
        try {
          const date = typeof editData.cessationdate === 'string' ? parseISO(editData.cessationdate) : new Date(editData.cessationdate as any);
          setQuitDate(date);
        } catch (e) {
          setQuitDate(new Date());
        }
      }
      if (editData.cuser) {
        setOperationUser(editData.cuser);
      }
      if (editData.ctime) {
        try {
          const date = typeof editData.ctime === 'string' ? parseISO(editData.ctime) : new Date(editData.ctime);
          setOperationDate(date);
        } catch (e) {
          setOperationDate(new Date());
        }
      }
    }
  }, [open, editData]);

  // Modal açıldığında snackbar'ı temizle
  useEffect(() => {
    if (open) {
      setSnackbarOpen(false);
      setSnackbarMessage("");
    }
  }, [open]);

  // Modal kapandığında state'leri temizle
  useEffect(() => {
    if (!open) {
      setHabit("");
      setHabitName("");
      setSelectedItem(null);
      setType("");
      setQuitDate(new Date());
      setAmountDaily("");
      setDurationYear("");
      setNote("");
      setOperationDate(new Date());
      setHabitId(null);
      setSnackbarOpen(false);
      setSnackbarMessage("");
    }
  }, [open]);

  // Tarih formatını "2025-12-26T09:11:38" formatına çevir (milisaniye ve Z olmadan)
  const formatDateTime = (date: Date | null): string | undefined => {
    if (!date) return undefined;
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const seconds = String(d.getSeconds()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  };

  const handleSave = async () => {
    if (!patientId) {
      setSnackbarMessage(t("habit.errors.patient-id-not-found"));
      setSnackbarType("error");
      setSnackbarOpen(true);
      return;
    }

    // Edit modunda güncelleme
    if (editData) {
      if (!editData.id) {
        setSnackbarMessage(t("habit.errors.id-not-found"));
        setSnackbarType("error");
        setSnackbarOpen(true);
        return;
      }

      setLoading(true);
      try {
        await mcGeneralDiseaseService.updateHabit({
          id: editData.id,
          dailyuseamount: amountDaily || "",
          typeofmaterial: type || "",
          usagetime: durationYear || "",
          cessationdate: formatDateTime(quitDate),
          note: note || "",
        });

        setSnackbarMessage(t("habit.success.save-success"));
        setSnackbarType("success");
        setSnackbarOpen(true);
        setTimeout(() => {
          onClose();
        }, 1500);
      } catch (error: any) {
        console.error("Alışkanlık güncelleme hatası:", error);
        const errorMessage = error?.response?.data?.error || error?.error || t("habit.errors.save-error");
        setSnackbarMessage(errorMessage);
        setSnackbarType("error");
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Yeni kayıt
    if (!habitId) {
      setSnackbarMessage(t("habit.errors.habit-not-selected"));
      setSnackbarType("error");
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);
    try {
      const requestData = {
        ptid: Number(patientId),
        processid: habitId,
        dailyuseamount: amountDaily || "",
        typeofmaterial: type || "",
        usagetime: durationYear || "",
        cessationdate: formatDateTime(quitDate),
        note: note || "",
      };

      await mcGeneralDiseaseService.addHabit(requestData);
      setSnackbarMessage(t("habit.success.save-success"));
      setSnackbarType("success");
      setSnackbarOpen(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error: any) {
      console.error("Alışkanlık kaydetme hatası:", error);
      const errorMessage = error?.response?.data?.error || error?.error || t("habit.errors.save-error");
      setSnackbarMessage(errorMessage);
      setSnackbarType("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <OpenComponentDialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      hideHeader
      showPatientData={false}
    >
      <Box
        sx={{
          py: { xs: 2, sm: 3 },
          px: { xs: 1.5, sm: 2 },
          width: "100%",
          maxWidth: "100%",
        }}
      >
        {/* Başlık */}
        <Box
          sx={{
            width: "100%",
            backgroundColor: "#f4f7fe",
            borderBottom: "1px solid #e0e0e0",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "flex-start",
            py: 1.5,
            px: 2.5,
            mb: 2,
          }}
        >
          <ByzMuiTypography isTitle={true} sx={{ fontSize: 20, lineHeight: "22px", color: "#111827" }}>
            {t("habit.title")}
          </ByzMuiTypography>
        </Box>

        {/* Save / Close */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            gap: 1,
          }}
        >
          <ByzMuiSaveButton onClick={handleSave} disabled={loading} />
          <Box sx={{ flex: 1 }} />
          <ByzMuiCloseButton onClick={onClose} />
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Hasta bilgisi kartı (grafiksiz) */}
        <Box sx={{ mb: 2 }}>
          <PatientDatas patientId={patientId} isCreateMedCard noMargin />
        </Box>

        {/* Form alanları */}
        <Box
          sx={{
            mt: 1,
            width: { xs: "100%", md: "60%" },
          }}
        >
          {/* Bölüm başlığı - Alışkanlık */}
          <Box
            sx={{
              backgroundColor: "#F4F7FE",
              borderBottom: "1px solid #E0E0E0",
              mb: 1.5,
              px: 3,
              py: 1,
            }}
          >
            <ByzMuiTypography sx={{ fontSize: 16 }}>
              {t("habit.title")}
            </ByzMuiTypography>
          </Box>

          <Grid container spacing={0}>
            {/* İşlem Tarihi */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("habit.operation-date")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiDateTimePicker
                    value={operationDate}
                    onChange={(val) => setOperationDate(val as Date | null)}
                    width="100%"
                    disabled
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* İşlem Yapan Kullanıcı */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("habit.operation-user")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiTextField
                    label="İşlem Yapan Kullanıcı"
                    value={operationUser}
                    onChange={(e) => setOperationUser(e.target.value)}
                    fullWidth
                    cantChange
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Adı (select) */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("habit.habit")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzSelectListPopupInput
                    label={t("buttons.chose") || "Seçiniz"}
                    placeholder={t("buttons.chose") || "Seçiniz"}
                    value={habitName}
                    selectedItem={selectedItem}
                    disabled={!!editData}
                    singleSelection={true}
                    onChange={(value: any) => {
                      // Tek seçim için direkt item'ı al
                      const item = value;
                      if (item) {
                        setSelectedItem(item);
                        setHabitId(item?.id || item?.ID || null);
                        setHabit(String(item?.id || item?.ID || ""));
                        setHabitName(item?.name || item?.NAME || "");
                      } else {
                        setSelectedItem(null);
                        setHabitId(null);
                        setHabit("");
                        setHabitName("");
                      }
                    }}
                    url="/reference/api/McGeneralDisease/field-habit"
                    method="POST"
                    postData={{
                      offid: offid,
                    }}
                    dialogTitle={t("habit.dialog-title")}
                    displayField="name"
                    columns={[
                      { field: "id", headerName: "ID", width: 100 },
                      { field: "name", headerName: t("habit.habit"), flex: 1 },
                    ]}
                    dialogShowOkButton={true}
                    rowID={patientId}
                    showPatientData={false}
                    fullWidth
                    sx={{ width: "100%" }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Türü (Madde ise) */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("habit.type")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiTextField
                    label="Giriniz"
                    placeholder={t("habit.type")}
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Bırakma Tarihi */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("habit.quit-date")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiDateTimePicker
                    value={quitDate}
                    onChange={(val) => setQuitDate(val as Date | null)}
                    width="100%"
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Miktar (Adet/Günlük) */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("habit.amount-daily")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiTextField
                    label="Giriniz"
                    placeholder={t("habit.amount-daily")}
                    value={amountDaily}
                    onChange={(e) => setAmountDaily(e.target.value)}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Süre (Yıl) */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("habit.duration-year")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiTextField
                    label="Giriniz"
                    placeholder={t("habit.duration-year")}
                    value={durationYear}
                    onChange={(e) => setDurationYear(e.target.value)}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Not */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("habit.note")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiTextField
                    label="Giriniz"
                    placeholder={t("habit.note") || "Not"}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Loading Overlay */}
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <CircularProgress size={60} />
        </Box>
      )}

      {/* Snackbar */}
      <ByzMuiSnackBar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        title={snackbarType === "success" ? t("common.success") : t("common.error")}
        message={snackbarMessage}
        type={snackbarType}
      />
    </OpenComponentDialog>
  );
};

export default HabitsModal;
